import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { photographerService } from '../../services/photographerService';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Image from '../../components/AppImage';
import { formatNumber } from '../../utils/formatNumber';

const Favorites = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedForCompare, setSelectedForCompare] = useState([]);
  const [showCompare, setShowCompare] = useState(false);

  useEffect(() => {
    loadFavorites();
  }, [userProfile]);

  const loadFavorites = async () => {
    try {
      setLoading(true);
      if (!userProfile?.id) return;

      // Load favorites from database (assuming a favorites table exists)
      // For MVP, we'll use a simple approach with localStorage or a favorites table
      const { data, error } = await supabase
        .from('favorites')
        .select('photographer_id')
        .eq('customer_id', userProfile.id);

      if (error && error.code !== '42P01') {
        // Table doesn't exist, use localStorage as fallback
        const stored = localStorage.getItem(`favorites_${userProfile.id}`);
        const favoriteIds = stored ? JSON.parse(stored) : [];
        
        if (favoriteIds.length > 0) {
          const photographers = await Promise.all(
            favoriteIds.map((id) => photographerService.getPhotographer(id))
          );
          setFavorites(photographers.filter(Boolean));
        }
      } else if (data && data.length > 0) {
        const photographers = await Promise.all(
          data.map((fav) => photographerService.getPhotographer(fav.photographer_id))
        );
        setFavorites(photographers.filter(Boolean));
      }
    } catch (error) {
      console.error('Failed to load favorites:', error);
      // Fallback to localStorage
      if (userProfile?.id) {
        const stored = localStorage.getItem(`favorites_${userProfile.id}`);
        const favoriteIds = stored ? JSON.parse(stored) : [];
        if (favoriteIds.length > 0) {
          const photographers = await Promise.all(
            favoriteIds.map((id) => photographerService.getPhotographer(id))
          );
          setFavorites(photographers.filter(Boolean));
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFavorite = async (photographerId) => {
    try {
      if (!userProfile?.id) return;

      // Remove from database
      const { error } = await supabase
        .from('favorites')
        .delete()
        .eq('customer_id', userProfile.id)
        .eq('photographer_id', photographerId);

      // Also remove from localStorage as backup
      const stored = localStorage.getItem(`favorites_${userProfile.id}`);
      const favoriteIds = stored ? JSON.parse(stored) : [];
      const updated = favoriteIds.filter((id) => id !== photographerId);
      localStorage.setItem(`favorites_${userProfile.id}`, JSON.stringify(updated));

      setFavorites((prev) => prev.filter((p) => (p.userId || p.user_id) !== photographerId));
    } catch (error) {
      console.error('Failed to remove favorite:', error);
      // Fallback to localStorage only
      if (userProfile?.id) {
        const stored = localStorage.getItem(`favorites_${userProfile.id}`);
        const favoriteIds = stored ? JSON.parse(stored) : [];
        const updated = favoriteIds.filter((id) => id !== photographerId);
        localStorage.setItem(`favorites_${userProfile.id}`, JSON.stringify(updated));
        setFavorites((prev) => prev.filter((p) => (p.userId || p.user_id) !== photographerId));
      }
    }
  };

  const handleToggleCompare = (photographerId) => {
    setSelectedForCompare((prev) => {
      if (prev.includes(photographerId)) {
        return prev.filter((id) => id !== photographerId);
      } else if (prev.length < 3) {
        return [...prev, photographerId];
      } else {
        alert('يمكنك مقارنة 3 مصورين كحد أقصى');
        return prev;
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-slate-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">المفضلة</h1>
          <p className="text-slate-600">المصورون المحفوظون لديك</p>
        </div>
        <div className="flex gap-3">
          {selectedForCompare.length > 0 && (
            <Button
              variant="default"
              onClick={() => setShowCompare(true)}
              iconName="Compare"
            >
              مقارنة ({selectedForCompare.length})
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => navigate('/browse-photographers')}
            iconName="Plus"
            iconPosition="right"
          >
            إضافة مصورين
          </Button>
        </div>
      </div>

      {/* Favorites Grid */}
      {favorites.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {favorites.map((photographer) => {
            const photographerId = photographer.userId || photographer.user_id;
            const isSelected = selectedForCompare.includes(photographerId);
            return (
              <div
                key={photographerId}
                className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <Image
                    src={
                      photographer.user?.avatar_url ||
                      photographer.user?.avatarUrl ||
                      'https://img.rocket.new/generatedImages/rocket_gen_img_1161a7ebd-1763294739154.png'
                    }
                    alt={photographer.user?.full_name || photographer.user?.fullName || 'مصور'}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <button
                    onClick={() => handleRemoveFavorite(photographerId)}
                    className="p-2 text-error hover:bg-error/10 rounded-lg transition-colors"
                  >
                    <Icon name="Heart" size={20} className="fill-current" />
                  </button>
                </div>

                <h3 className="text-lg font-bold text-slate-900 mb-2">
                  {photographer.user?.full_name || photographer.user?.fullName || 'مصور'}
                </h3>
                <p className="text-slate-600 mb-3">{photographer.city || 'الرياض'}</p>

                {/* Specialties */}
                {photographer.specializations && photographer.specializations.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {photographer.specializations.slice(0, 3).map((spec, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-primary/10 text-primary rounded-lg text-xs font-medium"
                      >
                        {spec}
                      </span>
                    ))}
                  </div>
                )}

                {/* Rating */}
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="Star" size={16} className="text-warning fill-warning" />
                  <span className="font-semibold text-slate-900">
                    {formatNumber(photographer.rating || 0)}
                  </span>
                  <span className="text-slate-500 text-sm">
                    ({formatNumber(photographer.total_bookings || photographer.totalBookings || 0)})
                  </span>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      navigate('/photographer-profile', {
                        state: { photographerId },
                      })
                    }
                    className="flex-1"
                    iconName="Eye"
                  >
                    عرض الملف
                  </Button>
                  <button
                    onClick={() => handleToggleCompare(photographerId)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isSelected
                        ? 'bg-button text-button-foreground'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    {isSelected ? '✓' : 'مقارنة'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 text-center">
          <Icon name="Heart" size={64} className="mx-auto mb-4 text-slate-400" />
          <h3 className="text-xl font-semibold text-slate-900 mb-2">لا توجد مفضلات</h3>
          <p className="text-slate-600 mb-6">ابدأ بإضافة مصورين إلى قائمة المفضلة</p>
          <Button
            variant="default"
            onClick={() => navigate('/browse-photographers')}
            iconName="Search"
            iconPosition="right"
          >
            تصفح المصورين
          </Button>
        </div>
      )}

      {/* Compare Modal */}
      {showCompare && selectedForCompare.length > 0 && (
        <CompareModal
          photographerIds={selectedForCompare}
          favorites={favorites}
          onClose={() => {
            setShowCompare(false);
            setSelectedForCompare([]);
          }}
        />
      )}
    </div>
  );
};

// Compare Modal Component
const CompareModal = ({ photographerIds, favorites, onClose }) => {
  const photographers = favorites.filter(
    (p) => photographerIds.includes(p.userId || p.user_id)
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" dir="rtl">
      <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-slate-900">مقارنة المصورين</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {photographers.map((photographer) => {
              const photographerId = photographer.userId || photographer.user_id;
              return (
                <div key={photographerId} className="border border-slate-200 rounded-xl p-4">
                  <Image
                    src={
                      photographer.user?.avatar_url ||
                      photographer.user?.avatarUrl ||
                      'https://img.rocket.new/generatedImages/rocket_gen_img_1161a7ebd-1763294739154.png'
                    }
                    alt={photographer.user?.full_name || photographer.user?.fullName || 'مصور'}
                    className="w-full h-48 object-cover rounded-lg mb-4"
                  />
                  <h3 className="text-lg font-bold text-slate-900 mb-2">
                    {photographer.user?.full_name || photographer.user?.fullName || 'مصور'}
                  </h3>
                  <p className="text-slate-600 mb-4">{photographer.city || 'الرياض'}</p>

                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-slate-600 mb-1">التخصصات</p>
                      <div className="flex flex-wrap gap-1">
                        {(photographer.specializations || []).slice(0, 3).map((spec, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-primary/10 text-primary rounded text-xs"
                          >
                            {spec}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div>
                      <p className="text-sm text-slate-600 mb-1">التقييم</p>
                      <div className="flex items-center gap-1">
                        <Icon name="Star" size={16} className="text-warning fill-warning" />
                        <span className="font-semibold">{formatNumber(photographer.rating || 0)}</span>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm text-slate-600 mb-1">المعدات المتاحة</p>
                      <div className="flex flex-wrap gap-1">
                        {(photographer.languages || []).map((lang, index) => (
                          <span key={index} className="px-2 py-1 bg-slate-100 rounded text-xs">
                            {lang}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div>
                      <p className="text-sm text-slate-600 mb-1">الحالة</p>
                      <span className="px-2 py-1 bg-success/10 text-success rounded text-xs">
                        متاح
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Favorites;
